import React, { useState } from "react";
import { t } from "../../../i18n";
import { copyToClipboard } from "../../../util/CopyToClipboard";

interface Props {
  secret: string;
}

type CopyOutcome = "untried" | "copied" | "failed";

/**
 * Presents a freshly minted token value. The value reaches the browser once and is held nowhere
 * else, so this is the admin's only chance to take it — hence the copy control and the warning
 * sitting above the value rather than beside it.
 */
export function MintedSecretDisplay({ secret }: Props): JSX.Element {
  const [copyOutcome, setCopyOutcome] = useState<CopyOutcome>("untried");

  async function copySecret(): Promise<void> {
    setCopyOutcome((await copyToClipboard(secret)) ? "copied" : "failed");
  }

  return (
    <div data-testid="minted-token">
      <div
        className="alert alert-warning"
        data-testid="minted-token-warning"
      >
        {t(
          "api-tokens.secret.warning",
          undefined,
          "This is the only time the API token is shown. It cannot be retrieved again — if you lose it, revoke it and create a new one.",
        )}
      </div>

      <div className="form-group">
        <label htmlFor="minted-token-secret">{t("api-tokens.secret.field", undefined, "API token")}</label>
        <input
          id="minted-token-secret"
          data-testid="minted-token-secret"
          className="form-control"
          type="text"
          readOnly={true}
          value={secret}
          onFocus={(event) => event.target.select()}
        />
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        data-testid="minted-token-copy"
        onClick={() => copySecret()}
      >
        {copyOutcome === "copied" ? t("api-tokens.secret.copied", undefined, "Copied") : t("api-tokens.secret.copy", undefined, "Copy")}
      </button>

      {copyOutcome === "failed" && (
        <div
          className="text-danger mt-2"
          data-testid="minted-token-copy-failed"
        >
          {t("api-tokens.secret.copy-failed", undefined, "Could not copy to the clipboard. Select the API token and copy it manually.")}
        </div>
      )}
    </div>
  );
}
